import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { Shield, Mail, Database, Trash2, Eye, FileText, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicyPage() {
  const lastUpdated = "February 2025";

  return (
    <MainLayout>
      <SEOHead
        title="Privacy Policy | Jigsaw Events Hub"
        description="Learn how Jigsaw Conferences collects, uses, and protects your personal data. GDPR compliant privacy policy for events.jigsawconferences.co.uk"
        url="https://events.jigsawconferences.co.uk/privacy"
      />

      <div className="bg-gray-50 min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Jigsaw Conferences Ltd ("we", "us", "our") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website <strong>events.jigsawconferences.co.uk</strong> 
                (the "Site").
              </p>
              <p className="text-gray-700">
                We comply with the UK General Data Protection Regulation (UK GDPR) and the Data 
                Protection Act 2018. Our registered address is: Jigsaw Conferences Ltd, United Kingdom.
              </p>
            </section>

            {/* Data We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-blue-600" />
                2. Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Personal Data You Provide:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Contact Information:</strong> Name, email address, phone number when you enquire about events or accommodation</li>
                <li><strong>Event Preferences:</strong> Events you're interested in, calendar subscriptions, sector preferences</li>
                <li><strong>Referral Program Data:</strong> If you participate in our Event Boost referral program</li>
                <li><strong>Communication Data:</strong> Messages you send to us via contact forms</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Data Collected Automatically:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Usage Data:</strong> Pages visited, time on site, click patterns (only with your consent)</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                <li><strong>Cookies:</strong> See our <a href="/cookies" className="text-blue-600 hover:underline">Cookie Policy</a> for details</li>
              </ul>
            </section>

            {/* How We Use Data */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>To respond to your enquiries about events and accommodation</li>
                <li>To send you calendar updates for events you've subscribed to</li>
                <li>To manage your participation in referral programs</li>
                <li>To improve our website and services (with your consent)</li>
                <li>To send marketing communications (only with your explicit consent)</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Legal Basis */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
              <p className="text-gray-700 mb-4">We process your data based on:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Consent:</strong> For marketing emails, analytics cookies, and personalization</li>
                <li><strong>Contractual Necessity:</strong> To provide services you've requested (e.g., accommodation enquiries)</li>
                <li><strong>Legitimate Interests:</strong> To improve our services and prevent fraud</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Who We Share Your Data With</h2>
              <p className="text-gray-700 mb-4">We may share your personal data with:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Hotels & Venues:</strong> When you request accommodation quotes</li>
                <li><strong>Event Organisers:</strong> When you enquire about specific events</li>
                <li><strong>Service Providers:</strong> Email services (Microsoft 365), analytics (Google Analytics 4 with consent)</li>
                <li><strong>Legal Authorities:</strong> If required by law</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We do not sell your personal data to third parties.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-blue-600" />
                6. Your Rights Under GDPR
              </h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal data:</p>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Access</h4>
                  <p className="text-sm text-gray-600">Request a copy of the personal data we hold about you</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Rectification</h4>
                  <p className="text-sm text-gray-600">Request correction of inaccurate or incomplete data</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Erasure</h4>
                  <p className="text-sm text-gray-600">Request deletion of your personal data ("right to be forgotten")</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Restriction</h4>
                  <p className="text-sm text-gray-600">Request that we limit processing of your data</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Portability</h4>
                  <p className="text-sm text-gray-600">Request your data in a machine-readable format</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Right to Object</h4>
                  <p className="text-sm text-gray-600">Object to processing based on legitimate interests</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Exercise Your Rights</h4>
                <p className="text-blue-800 mb-3">
                  To exercise any of these rights, please contact our Data Protection team:
                </p>
                <p className="text-blue-800">
                  <strong>Email:</strong> <a href="mailto:privacy@jigsawconferences.co.uk" className="underline">privacy@jigsawconferences.co.uk</a><br />
                  <strong>Response Time:</strong> Within 30 days
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 mb-4">We retain your personal data for:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Enquiry Data:</strong> 2 years from your last interaction</li>
                <li><strong>Calendar Subscriptions:</strong> Until you unsubscribe</li>
                <li><strong>Referral Program Data:</strong> 3 years after the event</li>
                <li><strong>Analytics Data:</strong> 14 months (anonymized after 2 months)</li>
              </ul>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700">
                Your data may be processed by service providers outside the UK/EEA. Where this 
                occurs, we ensure appropriate safeguards are in place, such as Standard Contractual 
                Clauses approved by the UK ICO, or the service provider is certified under an 
                approved transfer mechanism.
              </p>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organisational measures to protect your 
                personal data, including encryption in transit (HTTPS), secure data storage, 
                access controls, and regular security assessments.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Cookie className="w-6 h-6 text-blue-600" />
                10. Cookies
              </h2>
              <p className="text-gray-700 mb-4">
                We use cookies to enhance your experience. You can manage your cookie preferences 
                at any time using the cookie settings panel on our website.
              </p>
              <a href="/cookies">
                <Button variant="outline">
                  View Cookie Policy
                </Button>
              </a>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-600" />
                11. Contact Us
              </h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy or wish to exercise your 
                  rights, please contact us:
                </p>
                <p className="text-gray-700">
                  <strong>Data Protection Officer</strong><br />
                  Jigsaw Conferences Ltd<br />
                  Email: <a href="mailto:privacy@jigsawconferences.co.uk" className="text-blue-600 hover:underline">privacy@jigsawconferences.co.uk</a><br />
                  Phone: 0800 121 4470
                </p>
                <p className="text-gray-700 mt-4">
                  You also have the right to lodge a complaint with the Information Commissioner's 
                  Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ico.org.uk</a>.
                </p>
              </div>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any 
                significant changes by posting the new policy on this page and updating the 
                "Last updated" date.
              </p>
            </section>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
